import axios, { AxiosError } from 'axios';
import { AppMedError } from '../../error/AppMedError.ts';
import { notifications } from '@mantine/notifications';
import React from 'react';
import { getVersionMismatchNotificationConfig } from '../../notifications/VersionMismatchNotification';

export const VITE_API_URL = import.meta.env.VITE_API_URL;
export const VITE_BASE_URL = import.meta.env.VITE_BASE_URL;

const ApiManager = axios.create({
    baseURL: VITE_API_URL,
    timeout: 120000,
    withCredentials: true,
});

ApiManager.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

ApiManager.interceptors.request.use((config) => {
    config.headers['X-Client-Commit'] = __GIT_COMMIT_HASH__;
    return config;
});

// Track the notification so we don't show multiple ones
let versionMismatchNotificationShown = false;
let currentClientVersion = localStorage.getItem('app-current-client-version');
let lastKnownServerVersion = localStorage.getItem('app-last-known-server-version');
// Add a flag to track if this is the first request after page load
let isFirstRequestAfterPageLoad = true;

// Function to check version and notify if needed
const checkVersionAndNotify = (serverVersion: string) => {
    if (isFirstRequestAfterPageLoad) {
        currentClientVersion = lastKnownServerVersion = serverVersion;
        localStorage.setItem('app-current-client-version', serverVersion);
        localStorage.setItem('app-last-known-server-version', serverVersion);
        isFirstRequestAfterPageLoad = false;
        return;
    }

    if (!currentClientVersion) {
        currentClientVersion = serverVersion;
        localStorage.setItem('app-current-client-version', serverVersion);
    }

    if (lastKnownServerVersion !== serverVersion) {
        lastKnownServerVersion = serverVersion;
        localStorage.setItem('app-last-known-server-version', serverVersion);
    }

    if (serverVersion !== currentClientVersion && !versionMismatchNotificationShown) {
        notifications.show(
            getVersionMismatchNotificationConfig(() => {
                versionMismatchNotificationShown = false;
            }),
        );
        versionMismatchNotificationShown = true;
    }
};

ApiManager.interceptors.response.use(
    (response) => {
        const serverVersion = response.headers['x-app-version'];
        if (serverVersion) 
            checkVersionAndNotify(serverVersion);
        
        // Handle redirects
        switch (response.status) {
            case 300:
            case 301:
            case 302:
            case 303:
            case 307:
            case 308:
                window.location.href = response.headers.location;
                break;
            default:
                break;
        }
        return response;
    },
    (error) => {
        const serverVersion = error.response.headers['x-app-version'];
        if (serverVersion) 
            checkVersionAndNotify(serverVersion);
        
        const axiosError = error as AxiosError;

        if (!axiosError.response) 
            return Promise.reject(new AppMedError('Erreur inconnue', 'Unknown', 'UNKNOWN_ERROR'));

        const errorResponse: AppMedError = AppMedError.fromApiResponse(
            axiosError.response.data as AppMedError,
            axiosError.response.status,
        );

        // Check if global error handling should be suppressed for this request
        const suppressGlobalErrorHandling = axiosError.config?.headers?.['X-Suppress-Global-Error-Handling'] === 'true';

        switch (errorResponse.httpStatusCode) {
          case 401:
          case 403:
            if ((!axiosError.response.data || typeof axiosError.response.data !== 'object') && window.location.pathname !== '/login') { //if there is no message it means that it blocked at the authorization attribute attribute level on the backend
              localStorage.removeItem('currentUser');
              window.location.href = '/login';
            }
          break;
        }

        // Only show notifications if global error handling is not suppressed
        if (!suppressGlobalErrorHandling) {
            switch (errorResponse.httpStatusCode) {
                case 401:
                case 400:
                case 403:
                case 409:
                case 500:
                    notifications.show({
                        title: errorResponse.title,
                        message: errorResponse.message ? (
                            <div>
                                {errorResponse.message.split('\\n').map((line, index) => (
                                    <React.Fragment key={index}>
                                        {line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </div>
                        ) : (
                            <>{typeof axiosError.response.data === 'string' ? axiosError.response.data : 'UNKNOW ERROR'}</>
                        ),
                        color: 'yellow',
                        withCloseButton: true,
                        autoClose: 5000,
                    });
                    break;
                case 422:
                    // Don't show notifications for 422 errors (email validation errors)
                    // These will be handled by the specific components
                    break;
                default:
            }
        }

        // Handle clinic subscription access errors (402 Payment Required)
        // The backend SubscriptionMiddleware will redirect to /no-seats when needed
        // This frontend check is kept as a fallback for API-level 402 responses
        if (axiosError.response?.status === 402) {
            const errorData = axiosError.response.data as any;
            
            if (errorData && errorData.error) {
                // Redirect to no-seats page for all clinic access errors
                if (!window.location.pathname.includes('/no-seats')) 
                    window.location.href = '/patientDashboard/no-seats';
                
                return Promise.reject(new Error(errorData.error));
            }
        }

        return Promise.reject(errorResponse);
    },
);

export { ApiManager };
